import { isEmpty } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import ContactComponent from "../../../components/ContactComponent";
import { useAuthentication } from "../../../context/authentication";
import { useContacts } from "../../../context/contacts";
import { useLocations } from "../../../context/locations";
import { Contact } from "../../../utils/types";
import ContactFormSidebar from "../../Workspace/ContactsData/ContactFormSidebar";

const LocationContactsTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const { wrapActionWithAuth } = useAuthentication();
  const { currentLocation, locations, setEditLocationConfig } = useLocations();
  const { contacts } = useContacts();
  const [openContactFormSidebar, setOpenContactFormSidebar] = useState(false);

  const location = useMemo(
    () =>
      currentLocation ||
      (props.match.params.id ? locations[props.match.params.id] : undefined),
    [currentLocation, locations, props.match.params.id]
  );

  const renderContactRow = useCallback(
    (contactUuid: string) => {
      const contact = contacts[contactUuid];

      if (!contact) return null;

      return (
        <tr key={contactUuid}>
          <td className="px-6 py-4 whitespace-nowrap">
            <Link
              className="relative flex items-center space-x-3 group"
              to={`/contacts/${contact.uuid}`}
            >
              <ContactComponent contactId={contact.uuid} />
            </Link>
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
              {contact.email ?? "-"}
            </p>
          </td>

          <td className="px-6 py-4 whitespace-nowrap">
            <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
              {contact.phone ?? "-"}
            </p>
          </td>
        </tr>
      );
    },
    [contacts]
  );

  if (!location) return null;

  return (
    <>
      <ContactFormSidebar
        contact={{ linked_location_ids: [props.match.params.id] } as Contact}
        open={openContactFormSidebar}
        toggleEditContactForm={() =>
          setOpenContactFormSidebar(!openContactFormSidebar)
        }
        showLinkedLocations={true}
      />

      <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full py-2 align-middle inline-block min-w-full">
          {!isEmpty(location.linked_contact_ids) ? (
            <div className="shadow w-full overflow-x-auto sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
                <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      <div className="flex items-center">
                        <span className="mr-1">
                          {location.linked_contact_ids.length} linked contact
                          {location.linked_contact_ids.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Email
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Phone
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                  {location.linked_contact_ids.map(renderContactRow)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex">
              <p className="text-sm text-gray-500 dark:text-gray-300">
                This location has no linked contact yet. Contacts can be linked
                from the{" "}
                <b
                  onClick={wrapActionWithAuth(() =>
                    setEditLocationConfig({
                      show: true,
                      location: currentLocation
                    })
                  )}
                  className="cursor-pointer hover:underline"
                >
                  location form
                </b>{" "}
                and from the{" "}
                <b
                  onClick={wrapActionWithAuth(() =>
                    setOpenContactFormSidebar(true)
                  )}
                  className="cursor-pointer hover:underline"
                >
                  contact form
                </b>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LocationContactsTab;
